import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import _ from 'lodash'
import logger from '@adc/logger'
import { filters, FilterSchema } from '@adc/blueprint'
import * as graph from '@adc/sdk'
import {
  PageLayout,
  Button,
  coreHooks,
  FilterToolbar,
  utils as btbUtils,
  useApplicationNotifications,
  NOTIFICATION_ROLES,
  // @ts-ignore no types
} from '@adc/ui-morpheus'
import DataTable, {
  SORT_DIRECTION,
  useSavedSearches,
  // @ts-ignore cannot find declaration file
} from '@adc/datatable'
// @ts-ignore no types
import { Link } from 'react-router-dom'
import { connect } from 'react-redux'
import { ROOT_CRUMB } from '../../constants/routing'
import { ORGANIZATION } from '../../constants/tableTypes'
import { fetchOrganizationsSeek } from '../../providers/organization'
import { useDebounce, useLatestRequest } from '../../hooks'
import {
  savedSearchProviders,
  MAX_PERSONAL_SAVED_SEARCHES,
} from '../../providers/savedSearches'
import { definition } from './filterDefinitions'
import { generateColumns } from './columns'
import { buildOrganizationsDSL } from './searchDsl'

const { getInitialPageParams } = filters.utils
const { useUrlPageStateSyncToUrl, useUrlPageStateSyncFromUrl } = filters.hooks

export const PAGE_SIZE = 200

type FilterAppModel = Record<string, unknown>

interface OrganizationRow {
  id: string
  name: string
  types: string[]
  aliases: string[]
  allowed_domains: string[]
  created_date: string
  updated_date: string
  [key: string]: unknown
}

const TEXT_SORT_FIELDS = ['name']

export interface OrganizationsGridProps {
  /** Injected by the connected container for GSW (global saved search) gating. */
  userAuthorities?: ReadonlyArray<string>
}

/**
 * Text fields cannot be sorted in OpenSearch ("fielddata is disabled"); sort on
 * their `.keyword` sub-field instead. Date/keyword fields sort on the raw name.
 */
const toSortField = (field: string): string =>
  TEXT_SORT_FIELDS.includes(field) ? `${field}.keyword` : field

export const OrganizationsGrid: React.FC<OrganizationsGridProps> = ({
  userAuthorities = [],
}) => {
  const notifications = useApplicationNotifications()
  const isMounted = coreHooks.useIsMounted()

  /**
   * Initial page params from URL
   */
  const [initialPageParams] = useState(() =>
    // Default to name ASC (matching the legacy grid). Without an explicit sort
    // default, getInitialPageParams falls back to created_date DESC.
    getInitialPageParams({
      pageSize: PAGE_SIZE,
      sort: { field: 'name', order: graph.enums.SortConditionOrder.Asc },
    })
  )

  /**
   * State
   */
  const [rows, setRows] = useState<OrganizationRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [browserStorage, setBrowserStorage] = useState(null)
  const [sortColumn, setSortColumn] = useState(() => {
    const { sort } = initialPageParams
    return {
      columnKey: sort?.field ?? 'name',
      sortField: sort?.field ? toSortField(sort.field) : 'name.keyword',
      direction: sort?.order?.toLowerCase() ?? (SORT_DIRECTION.ASC as string),
    }
  })

  /**
   * Search state
   */
  const [searchText, setSearchText] = useState(
    initialPageParams.searchText || ''
  )
  const debouncedSearchText = useDebounce(searchText, 300)
  const searchTextRef = useRef(searchText)
  const setSearchTextRef = useRef(setSearchText)
  searchTextRef.current = searchText
  setSearchTextRef.current = setSearchText

  /**
   * Filter state
   */
  /**
   * Active filter state drives useFilters. Held in state (not memo) so a
   * selected saved search can replace it — useFilters reacts to filterState
   * prop changes and recomputes its view models.
   */
  const [activeFilterState, setActiveFilterState] = useState<FilterAppModel>(
    () => (initialPageParams.filters || {}) as FilterAppModel
  )

  const { data, actions } = filters.hooks.useFilters<
    FilterAppModel,
    FilterSchema.FilterGroupViewModelCollection,
    never
  >({
    filterState: activeFilterState,
    definitionGenerator: definition,
  })

  const dataRef = useRef(data)
  const actionsRef = useRef(actions)
  dataRef.current = data
  actionsRef.current = actions

  /**
   * Pagination
   */
  const { Paginator, resetPagination, paginationState } =
    coreHooks.useOffsetPagination({
      totalCount,
      pageSize: PAGE_SIZE,
      initialOffset:
        ((initialPageParams.pagination?.currentPage || 1) - 1) * PAGE_SIZE,
    })

  /**
   * URL sync: state -> URL
   */
  useUrlPageStateSyncToUrl({
    filterState: data.filterState,
    sort: {
      field: sortColumn.columnKey,
      order:
        sortColumn.direction.toUpperCase() as graph.enums.SortConditionOrder,
    },
    pagination: {
      pageSize: paginationState?.pageSize ?? PAGE_SIZE,
      currentPage: paginationState?.currentPage ?? 1,
    },
    getCompactFilterState: actions.getCompactState || (() => ({})),
    searchText: debouncedSearchText,
  })

  /**
   * URL sync: URL -> state (back/forward nav)
   */
  useUrlPageStateSyncFromUrl({
    onUrlChange: ({ sort, searchText: urlSearchText }) => {
      if (sort?.field && sort?.order) {
        setSortColumn({
          columnKey: sort.field,
          sortField: toSortField(sort.field),
          direction: sort.order.toLowerCase(),
        })
      }
      if (urlSearchText) {
        setSearchText(urlSearchText)
      }
    },
  })

  /**
   * Browser storage for column prefs
   */
  const { PersistentBrowserStorage } = btbUtils
  coreHooks.useMountEffect(() => {
    const storage = new PersistentBrowserStorage({
      adapter: PersistentBrowserStorage.ADAPTERS.LOCAL_STORAGE,
    })
    setBrowserStorage(storage)
  })

  /**
   * Columns — use ref for rows to avoid dependency cycle
   */
  const rowsRef = useRef<OrganizationRow[]>(rows)
  rowsRef.current = rows

  const onRowAction = useCallback((rowIndex: number) => {
    const row = rowsRef.current[rowIndex]
    if (row) {
      window.location.href = `/organizations/${row.id}/edit`
    }
  }, [])

  // Double-clicking a row opens the org edit screen — the primary action from
  // this grid. Single-click still just selects/highlights the row. Reuses the
  // same navigation as the row's edit action icon.
  const onRowDoubleClick = useCallback(
    (_event: React.MouseEvent, rowIndex: number) => {
      onRowAction(rowIndex)
    },
    [onRowAction]
  )

  const columns = useMemo(
    () =>
      generateColumns({
        actionHandlers: { onRowAction },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onRowAction]
  )

  /**
   * Build DSL from filters + search
   */
  const getDsl = useCallback(() => {
    // Grid-local builder: multi-word text search + shared filter DSL. Keeps the
    // shared @adc/datatable text search untouched.
    return buildOrganizationsDSL({
      searchText: debouncedSearchText,
      filterState: data.filterState,
      columns,
      filterDefinitions: data.filterDefinitions,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText, data.filterState, data.filterDefinitions])

  /**
   * Fetch data using seek DSL (same pattern as organizationMembersGrid)
   */
  const prevDslRef = useRef<string>('')
  // Guards against out-of-order seek responses so a slow earlier request
  // can't overwrite the results of a newer one.
  const latestRequest = useLatestRequest()

  const fetchData = useCallback(() => {
    const dsl = getDsl()

    // Dedup: skip if nothing changed
    const dslKey = JSON.stringify({
      dsl,
      sortField: sortColumn.sortField,
      sortDirection: sortColumn.direction,
      offset: paginationState?.offset,
    })
    if (dslKey === prevDslRef.current) return
    prevDslRef.current = dslKey

    setIsLoading(true)

    const isCurrent = latestRequest()

    fetchOrganizationsSeek({
      dsl,
      fields: [
        'id',
        'name',
        'created_date',
        'types',
        'aliases',
        'updated_date',
        'allowed_domains',
        'groups.name',
        'groups.description',
      ],
      sortField: sortColumn.sortField || 'name.keyword',
      sortDirection: sortColumn.direction || 'asc',
      paginationState: {
        pageSize: paginationState?.pageSize ?? PAGE_SIZE,
        offset: paginationState?.offset ?? 0,
      },
    })
      .then(result => {
        if (!isCurrent()) return null // stale response, drop
        if (result.errors && result.errors.length > 0) {
          logger.error('Seek errors:', result.errors)
          setRows([])
          setTotalCount(0)
          return null
        }
        const content = result?.data?.content || []
        setTotalCount(
          result?.data?.total_count ?? result?.data?.total_elements ?? 0
        )
        // Transform types to title case (matching old apiToUiDataRowsTranslation)
        const rows = (content as unknown as OrganizationRow[]).map(row => ({
          ...row,
          types: (row.types || []).map(t => _.startCase(_.toLower(t))),
        }))
        setRows(rows)
        return null
      })
      .catch(error => {
        if (!isCurrent()) return // stale response, drop
        logger.error('Error fetching organizations', error)
        notifications?.toast?.open({
          role: NOTIFICATION_ROLES.ERROR,
          title: 'Error fetching organizations',
        })
        setRows([])
        setTotalCount(0)
      })
      .finally(() => {
        if (isCurrent()) setIsLoading(false)
      })
  }, [
    getDsl,
    sortColumn.sortField,
    sortColumn.direction,
    paginationState,
    notifications,
    latestRequest,
  ])

  useEffect(() => {
    fetchData()
  }, [
    fetchData,
    sortColumn.sortField,
    sortColumn.direction,
    debouncedSearchText,
    data.filterState,
    paginationState,
    refreshKey,
  ])

  /**
   * Reset pagination when filters/search change
   */
  const filterStateRef = useRef(data.filterState)
  const searchTextForResetRef = useRef(searchText)

  useEffect(() => {
    const filtersChanged = !_.isEqual(filterStateRef.current, data.filterState)
    const searchChanged = searchTextForResetRef.current !== debouncedSearchText

    if (filtersChanged || searchChanged) {
      resetPagination()
    }

    filterStateRef.current = data.filterState
    searchTextForResetRef.current = debouncedSearchText
  }, [data.filterState, debouncedSearchText, resetPagination])

  /**
   * Handlers
   */
  const onSortChange = useCallback(
    ([column]: {
      field: string
      sortField: string
      sortAscending: boolean
    }[]) => {
      setSortColumn({
        columnKey: column.field,
        sortField: column.sortField || toSortField(column.field),
        direction: column.sortAscending
          ? (SORT_DIRECTION.ASC as string)
          : (SORT_DIRECTION.DESC as string),
      })
    },
    []
  )

  const onRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1)
  }, [])

  /**
   * Saved searches (Redux-free, legacy wire format). Bridge between the grid's
   * blueprint filter state and the persisted FilterClause[] parameters.
   */
  const getFilterState = useCallback(
    (): FilterAppModel => dataRef.current.filterState,
    []
  )

  const applyFilterState = useCallback((filterState: FilterAppModel): void => {
    setActiveFilterState(filterState || {})
  }, [])

  const { savedSearches, savedSearchesActions } = useSavedSearches({
    namespace: ORGANIZATION,
    filterDefinitions: data.filterDefinitions,
    providers: savedSearchProviders,
    getFilterState,
    applyFilterState,
    maxPersonalSavedSearches: MAX_PERSONAL_SAVED_SEARCHES,
  })

  /**
   * Toolbar
   */
  const renderToolbar = useCallback(
    (props: { renderDataTableActions?: () => JSX.Element }) => (
      <FilterToolbar
        searchText={searchTextRef.current}
        setSearchText={setSearchTextRef.current}
        searchPlaceholder='Search by organization name'
        filterData={dataRef.current}
        filterActions={actionsRef.current}
        renderDataTableActions={props.renderDataTableActions}
      />
    ),
    []
  )

  /**
   * Page buttons
   */
  const buttons = [
    <Link to='/organizations/activitytimeline' key='activity-timeline-button'>
      <Button
        elmId='activity-timeline-button'
        role='secondary'
        size='small'
        style={{ marginRight: '6px' }}
      >
        View Activity Timeline
      </Button>
    </Link>,
    <Link to='/organizations/members' key='organization-members-button'>
      <Button
        elmId='organization-members-button'
        role='secondary'
        size='small'
        style={{ marginRight: '6px' }}
        color={Button.ButtonColors.Gray}
      >
        View Organization Members
      </Button>
    </Link>,
    <Link
      to='/organizations/create'
      data-elm-id='create_new_button'
      key='create_new_button'
    >
      <Button role='primary' size='small' style={{ whiteSpace: 'nowrap' }}>
        Create New
      </Button>
    </Link>,
  ]

  return (
    <PageLayout
      title='Organization Management'
      crumbs={[ROOT_CRUMB, 'Organization Management']}
      buttons={buttons}
    >
      <section className='full-height u-pt-2 u-mr-4 u-ml-4'>
        {/* DataTable reads persisted columns once at init; wait for mount so
            browserStorage (set post-mount, SSR-safe) is ready. */}
        {isMounted && (
          <DataTable
            columns={columns}
            rowData={rows}
            isLoading={isLoading}
            refreshData={onRefresh}
            changeSortBy={onSortChange}
            onRowDoubleClick={onRowDoubleClick}
            customToolbar={renderToolbar}
            primaryKeyField='id'
            entityKey='ORGANIZATIONS'
            enableSavedSearches
            savedSearches={savedSearches}
            savedSearchesActions={savedSearchesActions}
            userAuthorities={userAuthorities}
            notifications={notifications}
            sizes={{
              rowHeight: 40,
              headerHeight: 40,
              footerHeight: 110,
            }}
            customTableFooter={() => <div className='u-mt-1'>{Paginator}</div>}
            browserStorage={browserStorage}
            totalCount={totalCount}
          />
        )}
      </section>
    </PageLayout>
  )
}

/**
 * Connected container — injects `userAuthorities` for GSW (global saved
 * search) gating. Mirrors the CommandPalette store-wiring pattern; the grid itself
 * stays Redux-free and reads authorities via a single prop.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapStateToProps = (store: any): OrganizationsGridProps => ({
  userAuthorities: _.get(store, 'user.authorities', []),
})

export const OrganizationsGridContainer =
  connect(mapStateToProps)(OrganizationsGrid)

// Default export for route-level imports
export default OrganizationsGridContainer
